import { HeartHandshake } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUtmPayloadFromUrl, pushAnalyticsEvent } from '@/lib/analytics';
import { sendDonorClaimLink } from '@/lib/donor-claim';
import { MonthlyWarriorTeaser } from '@/components/donate/MonthlyWarriorTeaser';
import { getCheckoutSessionStatus } from '@/lib/stripe-billing';

function buildThankYouImpactTeaser(
  amountLabel: string | null,
  frequency: 'one_time' | 'monthly' | null
): string | null {
  if (!amountLabel || !frequency) return null;
  if (frequency === 'monthly') {
    return `Your ${amountLabel}/month gift helps fund lifesaving dialysis sessions for CKD warriors in the Philippines.`;
  }
  return `Your ${amountLabel} gift helps fund lifesaving dialysis sessions for CKD warriors in the Philippines.`;
}

interface DonateThankYouPageProps {
  isSignedIn: boolean;
}

export function DonateThankYouPage({ isSignedIn }: Readonly<DonateThankYouPageProps>): JSX.Element {
  const navigate = useNavigate();
  const [redirectCountdown, setRedirectCountdown] = useState(4);
  const [isVerifying, setIsVerifying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'pending' | 'unknown'>('unknown');
  const [donationFrequency, setDonationFrequency] = useState<'one_time' | 'monthly' | null>(null);
  const [donationAmountLabel, setDonationAmountLabel] = useState<string | null>(null);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [claimSending, setClaimSending] = useState(false);
  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [warriorNumber, setWarriorNumber] = useState<number | null>(null);
  const hasSuccessfulCheckout = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.get('checkout') === 'success';
  }, []);
  const checkoutSessionId = useMemo(() => {
    const params = new URLSearchParams(globalThis.location.search);
    return params.get('session_id');
  }, []);
  const impactTeaser = useMemo(
    () => buildThankYouImpactTeaser(donationAmountLabel, donationFrequency),
    [donationAmountLabel, donationFrequency]
  );

  useEffect(() => {
    if (!hasSuccessfulCheckout || !checkoutSessionId) {
      setPaymentStatus('unknown');
      return;
    }

    let isCancelled = false;

    const applyCheckoutStatus = (result: Awaited<ReturnType<typeof getCheckoutSessionStatus>>): void => {
      const status = result.paymentStatus === 'paid' ? 'paid' : 'pending';
      setPaymentStatus(status);
      setDonationFrequency(result.donationFrequency);
      setCheckoutEmail(result.customerEmail ?? '');
      if (typeof result.warriorNumber === 'number' && result.warriorNumber > 0) {
        setWarriorNumber(result.warriorNumber);
      }

      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (result.currency || 'usd').toUpperCase(),
        minimumFractionDigits: 0,
      }).format((result.amountTotal ?? 0) / 100);
      setDonationAmountLabel(formattedAmount);
    };

    const runVerification = async () => {
      setIsVerifying(true);
      try {
        const maxAttempts = 6;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const result = await getCheckoutSessionStatus(checkoutSessionId);
          if (isCancelled) return;

          applyCheckoutStatus(result);

          const hasWarrior =
            result.donationFrequency === 'monthly' &&
            typeof result.warriorNumber === 'number' &&
            result.warriorNumber > 0;
          const shouldRetry =
            result.paymentStatus === 'paid' &&
            result.donationFrequency === 'monthly' &&
            !hasWarrior &&
            attempt < maxAttempts - 1;

          if (!shouldRetry) break;
          await new Promise((resolve) => {
            globalThis.setTimeout(resolve, 1500);
          });
        }
      } catch {
        if (isCancelled) return;
        setPaymentStatus('unknown');
      } finally {
        if (!isCancelled) {
          setIsVerifying(false);
        }
      }
    };

    void runVerification();
    return () => {
      isCancelled = true;
    };
  }, [checkoutSessionId, hasSuccessfulCheckout]);

  const handleSendClaimLink = async (): Promise<void> => {
    if (!checkoutEmail.trim()) {
      setClaimMessage('Please enter the email you used for this donation.');
      return;
    }

    setClaimSending(true);
    setClaimMessage(null);
    try {
      await sendDonorClaimLink(checkoutEmail);
      pushAnalyticsEvent({
        event: 'donor_claim_started',
        claim_source: 'donate_thank_you',
      });
      setClaimMessage('Claim link sent. Check your inbox to secure your donor history.');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to send claim link right now. Please try again in a minute.';
      setClaimMessage(message);
    } finally {
      setClaimSending(false);
    }
  };

  useEffect(() => {
    pushAnalyticsEvent({
      event: 'donation_completed',
      conversion_name: 'donation_complete',
      ...getUtmPayloadFromUrl(globalThis.location.href),
    });
  }, []);

  useEffect(() => {
    if (donationFrequency !== 'monthly' || warriorNumber == null) return;
    pushAnalyticsEvent({
      event: 'monthly_warrior_signup',
      warrior_number: warriorNumber,
    });
  }, [donationFrequency, warriorNumber]);

  useEffect(() => {
    if (!hasSuccessfulCheckout || !isSignedIn) return undefined;

    const countdownInterval = globalThis.setInterval(() => {
      setRedirectCountdown((previous) => {
        if (previous <= 1) return 1;
        return previous - 1;
      });
    }, 1000);

    const redirectTimeout = globalThis.setTimeout(() => {
      navigate('/dashboard/donor', { replace: true });
    }, 4000);

    return () => {
      globalThis.clearInterval(countdownInterval);
      globalThis.clearTimeout(redirectTimeout);
    };
  }, [hasSuccessfulCheckout, isSignedIn, navigate]);

  return (
    <main className="max-w-3xl mx-auto px-4 py-14">
      <section className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
        <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <HeartHandshake size={24} />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Thank you for your support</h1>
        <p className="mt-3 text-gray-600">
          Your donation helps GSGO deliver emergency support and sustainable care initiatives for CKD
          warriors and their families.
        </p>
        {isVerifying ? (
          <p className="mt-4 text-sm font-medium text-gray-600">Verifying your payment...</p>
        ) : null}
        {!isVerifying && paymentStatus === 'paid' ? (
          <div className="mt-4 space-y-3 text-sm text-gray-700">
            <p className="font-medium text-green-700">
              Payment confirmed
              {donationAmountLabel ? ` for ${donationAmountLabel}` : ''}. Thank you for your generosity.
            </p>
            <p>
              A <strong>GSGO gift receipt and impact summary</strong> is on its way to your email (usually within a
              few minutes). That message is your primary receipt and shows how your gift supports dialysis care.
            </p>
            {impactTeaser ? <p className="text-blue-800">{impactTeaser}</p> : null}
            {donationFrequency === 'monthly' && warriorNumber != null ? (
              <MonthlyWarriorTeaser warriorNumber={warriorNumber} />
            ) : null}
            {donationFrequency === 'monthly' ? (
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-left">
                <p className="font-semibold text-blue-900">What happens next</p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-blue-900/90">
                  <li>Your monthly gift is active and renews automatically each month.</li>
                  <li>GSGO emails you a receipt and impact summary after each successful charge.</li>
                  <li>You can update or cancel anytime from your donor dashboard (Stripe Customer Portal).</li>
                  <li>
                    {warriorNumber != null
                      ? `Your Warrior #${warriorNumber} membership is active in GSGO's Monthly Warrior community.`
                      : "Your support is added to GSGO's Monthly Warrior community."}
                  </li>
                </ol>
              </div>
            ) : null}
            {!isSignedIn ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-left">
                <p className="font-semibold text-emerald-900">Claim your donor account</p>
                <p className="mt-1 text-emerald-900/90">
                  Use passwordless sign-in to unlock your dashboard, receipts, and monthly gift management.
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="email"
                    value={checkoutEmail}
                    onChange={(event) => setCheckoutEmail(event.target.value)}
                    className="w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm text-gray-900"
                    placeholder="you@example.com"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      void handleSendClaimLink();
                    }}
                    disabled={claimSending}
                    className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {claimSending ? 'Sending…' : 'Send claim link'}
                  </button>
                </div>
                {claimMessage ? <p className="mt-2 text-xs text-emerald-900">{claimMessage}</p> : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {!isVerifying && paymentStatus === 'pending' ? (
          <p className="mt-4 text-sm font-medium text-amber-700">
            Your checkout was completed and payment confirmation is still processing.
          </p>
        ) : null}
        {hasSuccessfulCheckout && isSignedIn ? (
          <p className="mt-4 text-sm font-medium text-blue-700">
            Redirecting you to your donor dashboard in {redirectCountdown}s...
          </p>
        ) : null}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {isSignedIn ? (
            <Link
              to="/dashboard/donor"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Open Donor Dashboard
            </Link>
          ) : null}
          <Link
            to="/"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Return Home
          </Link>
          <Link
            to="/warriors"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Meet CKD Warriors
          </Link>
        </div>
      </section>
    </main>
  );
}
